import React, { useState, useCallback } from 'react';
import FacetDropdowns from './FacetDropdowns';
import './GalleryFilterSidebar.css';

/**
 * Browse: facet controls to filter; when photos are selected, same controls assign metadata.
 */
export default function GalleryFilterSidebar({
  facetOptions,
  facetFilterRules,
  onFacetFilterRuleChange,
  onFacetFilterRuleOperatorToggle,
  onAddFacetFilterRule,
  onRemoveFacetFilterRule,
  onClearFacetFilters,
  untaggedOnly = false,
  onToggleUntagged,
  selectedPhotoCount = 0,
  onClearSelectedPhotos,
  useCategoryAssignment = false,
  assignFacets,
  onAssignFacetsChange,
  assignmentSaving = false,
  assignmentMessage = null,
  onClearMetadata,
  onSaveAssignment,
  assignmentDirty = false,
  /** When true, facet assignment controls are read-only (RBAC). */
  assignmentDisabled = false,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const filterRules = Array.isArray(facetFilterRules) && facetFilterRules.length > 0
    ? facetFilterRules
    : [{ id: 'default', operator: 'include', filters: {} }];

  const hasActiveFilters = filterRules.some((rule) =>
    Object.values(rule.filters || {}).some((v) => Array.isArray(v) && v.length > 0)
  );

  const wrapSelect = useCallback(
    (fn) => {
      fn();
      closeMobile();
    },
    [closeMobile]
  );

  return (
    <>
      <button
        type="button"
        className="gf-mobile-fab"
        aria-expanded={mobileOpen}
        aria-controls="gallery-filter-sidebar"
        onClick={() => setMobileOpen((o) => !o)}
      >
        {mobileOpen ? 'Close' : 'Filters'}
      </button>
      {mobileOpen && (
        <button
          type="button"
          className="gf-mobile-backdrop"
          aria-label="Close filters"
          onClick={closeMobile}
        />
      )}
      <div
        id="gallery-filter-sidebar"
        className={`gf-layout${mobileOpen ? ' gf-layout--open' : ''}`}
        aria-label="Filter and tag photos by people, places, pets, general, events, holidays, and camera"
      >
        <section className="gf-topbar">
          <div className="gf-filter-rules">
            {filterRules.map((rule, index) => {
              const isExclude = rule.operator === 'exclude';
              const canRemove = filterRules.length > 1;
              return (
                <div
                  key={rule.id}
                  className={`gf-filter-rule${isExclude ? ' gf-filter-rule--exclude' : ''}`}
                >
                  {filterRules.length > 1 && (
                    <div className="gf-filter-rule-head">
                      <span className="gf-filter-rule-title">Rule {index + 1}</span>
                      <span className="gf-filter-rule-mode">
                        {isExclude ? 'Exclude selected' : 'Include selected'}
                      </span>
                    </div>
                  )}
                  <div className="gf-hierarchical-wrap">
                    <FacetDropdowns
                      options={facetOptions}
                      values={rule.filters}
                      onValuesChange={(next) =>
                        wrapSelect(() => onFacetFilterRuleChange?.(rule.id, next))
                      }
                      hint={null}
                      multiMode="dropdown"
                      layout="toolbar"
                    />
                  </div>
                  <div className="gf-filter-rule-actions" aria-label={`Rule ${index + 1} actions`}>
                    <button
                      type="button"
                      className={`gf-filter-operator-btn${isExclude ? ' gf-filter-operator-btn--active' : ''}`}
                      aria-pressed={isExclude}
                      title={isExclude ? 'Switch this rule back to include' : 'Exclude selected values in this rule'}
                      onClick={() => wrapSelect(() => onFacetFilterRuleOperatorToggle?.(rule.id))}
                    >
                      !
                    </button>
                    <button
                      type="button"
                      className="gf-filter-operator-btn"
                      title="Add another filter rule"
                      onClick={() => wrapSelect(() => onAddFacetFilterRule?.())}
                    >
                      +
                    </button>
                    {canRemove && (
                      <button
                        type="button"
                        className="gf-filter-operator-btn gf-filter-operator-btn--remove"
                        title="Remove this filter rule"
                        onClick={() => wrapSelect(() => onRemoveFacetFilterRule?.(rule.id))}
                      >
                        ×
                      </button>
                    )}
                    <span className="gf-filter-rule-action-label">
                      {isExclude ? 'This row hides matching photos' : 'This row shows matching objects'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="gf-filter-toolbar" aria-label="Gallery filter actions">
            <div className="gf-filter-toolbar-inner">
              <button
                type="button"
                className={`gf-clear-filters-btn${untaggedOnly ? ' gf-clear-filters-btn--on' : ''}`}
                aria-pressed={untaggedOnly}
                onClick={() => wrapSelect(() => onToggleUntagged?.())}
              >
                Untagged
              </button>
              <button
                type="button"
                className="gf-clear-filters-btn"
                disabled={!hasActiveFilters}
                onClick={() => wrapSelect(onClearFacetFilters)}
              >
                Clear all filters
              </button>
              <p className="gf-filter-negation-hint">
                Use <kbd>!</kbd> to make a row exclude its selected values. Use <kbd>+</kbd> for another rule.
              </p>
            </div>
          </div>
        </section>

        <aside className="gf-sidebar">
          <div className="gf-sidebar-header">
            <h2 className="gf-title">On selected</h2>
            {selectedPhotoCount > 0 && typeof onClearSelectedPhotos === 'function' && (
              <button
                type="button"
                className="gf-clear-selected-btn"
                onClick={() => wrapSelect(() => onClearSelectedPhotos())}
              >
                Clear selected
              </button>
            )}
          </div>
          <section className="gf-section gf-section--assign gf-section--assign-column">
            {useCategoryAssignment ? (
              <>
                <div className="gf-assign-scroll">
                  <div className="gf-hierarchical-wrap gf-hierarchical-wrap--assign">
                    <FacetDropdowns
                      options={facetOptions}
                      values={assignFacets}
                      onValuesChange={(next) => wrapSelect(() => onAssignFacetsChange(next))}
                      disabled={assignmentSaving || assignmentDisabled}
                      hint={null}
                      multiMode="dropdown"
                    />
                  </div>
                  <div className="gf-assign-footer">
                    <div className="gf-assign-footer-btns">
                      <button
                        type="button"
                        className="gf-footer-action-btn"
                        disabled={assignmentSaving || assignmentDisabled || !assignmentDirty}
                        onClick={() => wrapSelect(() => onSaveAssignment?.())}
                      >
                        {assignmentSaving ? 'Saving…' : 'Save metadata'}
                      </button>
                      <button
                        type="button"
                        className="gf-footer-action-btn"
                        disabled={assignmentSaving || assignmentDisabled}
                        onClick={() => wrapSelect(() => onClearMetadata?.())}
                      >
                        Clear metadata
                      </button>
                    </div>
                  </div>
                </div>
                {assignmentMessage && (
                  <p
                    className={`gf-assign-status${
                      assignmentMessage === 'Saved' || assignmentMessage === 'Metadata cleared'
                        ? ' gf-assign-status--ok'
                        : ' gf-assign-status--err'
                    }`}
                  >
                    {assignmentMessage}
                  </p>
                )}
              </>
            ) : (
              <p className="gf-assign-hint gf-assign-hint--muted">Select one or more objects to assign facets.</p>
            )}
          </section>
        </aside>
      </div>
    </>
  );
}
